import * as React from "react"
import {useCallback} from "react"
import {Slot} from "@radix-ui/react-slot"
import {cva, type VariantProps} from "class-variance-authority"
import {Loader2} from "lucide-react"

import {cn} from "@/lib/utils"
import commonService from "@/services/common.service.ts";

export interface mixpanelEventType {
    name: string;
    properties?: any;
}

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none" +
    // " [&_svg:not([class*='size-'])]:size-4 " +
    "shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer disabled:cursor-not-allowed",
    {
        variants: {
            variant: {
                default:
                    "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
                destructive:
                    "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
                outline:
                    "border border-[var(--button-border)] border-1 bg-[var(--button-background)] shadow-xs hover:bg-[var(--button-background-highlight)] hover:text-white",
                secondary:
                    "bg-secondary text-background shadow-xs hover:bg-secondary/80",
                secondaryOutline:
                    "border border-input  text-[var(--custom-input-button-color)] shadow-xs bg-transparent rounded-md transition-colors hover:border-[var(--custom-button-highlight-border)] hover:text-primary",
                headerOutline:
                    "border border-[var(--button-border)] border-[0.75] text-secondary shadow-xs bg-transparent rounded-md transition-colors hover:border-[var(--custom-button-highlight-border)] hover:text-primary",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary font-normal underline-offset-2 decoration-1 hover:underline text-sm",
                text: "text-primary font-medium ml-[-16px]",
                textSecondary: "text-secondary font-medium",
                textDestructive:"text-destructive font-medium",
                linkUnderLine: "text-primary font-medium underline-offset-2 decoration-1 underline text-sm",
                secondaryForeground: "bg-[var(--button-border)] text-white shadow-xs",
                white: "bg-white text-black shadow-xs hover:bg-white/90 border border-gray-200",
                image:'',
                icon:''
                
            },
            size: {
                default: "h-9 px-4 py-2 has-[>svg]:px-3",
                xs: "text-xs h-5 rounded-sm font-normal gap-1.5 py-2 px-2 has-[>svg]:px-2.",
                sm: "h-5 rounded-md text-sm gap-1.5 py-3 px-3 has-[>svg]:px-2.5",
                md: " rounded-md text-sm gap-1.5  px-2 py-[6px]  has-[>svg]:px-2.5",
                lg: "h-11.5 rounded-md px-6 has-[>svg]:px-4",
                header: "h-8 px-3 py-2.5 text-sm font-medium",
                icon: "size-9",
                image:''
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const loaderSizeMapping = {
    default: "h-4 w-4", // default size for loading icon
    xs: "h-3 w-3",      // smaller size for xs button
    sm: "h-4 w-4",      // default size for small button
    md: "h-5 w-5",      // default size for medium button
    lg: "h-6 w-6",      // larger size for large button
    header: "h-4 w-4",  // header size
    icon: "h-4 w-4",    // icon size
    image: "h-4 w-4"    // image size
}

function Button({
                    className,
                    variant,
                    size,
                    asChild = false,
                    fullWidth = false,
                    prefixIcon,
                    suffixIcon,
                    loading = false,
                    loadingText,
                    showLoadingText = true,
                    children,
                    mixpanelEvent,
                    onClick,
...props
                }: React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
    asChild?: boolean,
    fullWidth?: boolean,
    prefixIcon?: React.ReactNode,
    suffixIcon?: React.ReactNode,
    loading?: boolean,
    loadingText?: string,
    showLoadingText?: boolean,
    mixpanelEvent?: mixpanelEventType
    onClick?: (event: any) => void
}) {
    const Comp = asChild ? Slot : "button"

    const handleOnClick = useCallback((event: any) => {
        if (mixpanelEvent) {
            commonService.handleMixpanelTrack(mixpanelEvent.name, {...mixpanelEvent?.properties})
        }
        if (onClick) {
            onClick(event);
        }
    }, [onClick, mixpanelEvent]);

    const loaderSizeClass = loaderSizeMapping[size || "default"];


    return (
        <Comp
            data-slot="button"
            className={cn(
                buttonVariants({variant, size, className}),
                fullWidth && "w-full"
            )}
            disabled={loading || props.disabled}
            onClick={handleOnClick}
            {...props}
        >
            {loading ? (
                <>
                    <Loader2 className={cn("animate-spin", loaderSizeClass)} />
                    {showLoadingText && (loadingText || "Loading...")}
                </>
            ) : (
                <>
                    {prefixIcon && <span>{prefixIcon}</span>}
                    {children}
                    {suffixIcon && <span>{suffixIcon}</span>}
                </>
            )}
        </Comp>
    )
}

export {Button, buttonVariants}
